import React, { useLayoutEffect, useRef, useState } from 'react'
import { useMediaQuery } from '@material-ui/core'
import { AdCreative, AssetApprovalStatus } from '@marketing-milk/interfaces'
import { useGetBusinessIfExists } from '@marketing-milk/frontend'
import { isDynamicCreative } from '../../campaigns/helpers/campaign.helpers'
import { LocalAdPreview } from 'components/ad-preview'
import { DynamicPoolPicker } from './DynamicPoolPicker'
import {
  DynamicCombination,
  FIRST_COMBINATION,
  clampCombination,
  copyPools,
  dynamicPools,
  pinnedCombination,
} from './dynamicCombination'
import { StatusPill } from './StatusPill'
import { useHistoryFits } from './ReviewHistory'
import { CreativeJourneyPanel, getCreativeJourney } from '../components/CreativeJourney'
import {
  PacketModalShell,
  modalGhostButtonStyle,
  modalInputStyle,
  modalPrimaryButtonStyle,
} from '../components/PacketModalShell'
import { dark, radius, status as statusColors, type as typo } from '../tokens'

type Props = {
  creative: AdCreative
  /** Pre-opens the reason form — used when the caller clicked Reject on the card. */
  startInReject?: boolean
  /** Which ad this is out of how many, counting ads only (theme slides aren't numbered). */
  position: { current: number; total: number }
  /** The theme this ad sits under, shown so the slide's framing survives the walk. */
  themeLabel?: string
  hasPrev: boolean
  hasNext: boolean
  onPrev: () => void
  onNext: () => void
  /**
   * Record a verdict. The page owns the mutation for the whole grid, so the modal
   * and the cards can't send different payloads.
   */
  onSubmitVerdict: (verdict: { approved: boolean; notes?: string }) => Promise<unknown>
  submitting: boolean
  /**
   * Employee-only affordances, supplied as capabilities rather than gated on a
   * cohort flag — the page decides who gets what, once, and this modal never
   * learns what a permission is.
   */
  onRemoveApproval?: () => void
  onOpenSource?: () => void
  /**
   * The ad's history and comments column. A capability like the two above: the
   * page withholds it from clients, and the modal only gives it a cell. Its own
   * open state must be held by the page too — this modal is remounted per ad.
   */
  historySlot?: React.ReactNode
  /** Called after a successful approve/reject — moves to the next ad, or closes if this was the last one. */
  onAdvance: () => void
  onClose: () => void
}

/** Modal padding, and the body height derived from it: overlay inset (16 top+bottom) + this. */
const SHELL_PADDING = 20
const BODY_HEIGHT = `calc(100vh - ${32 + SHELL_PADDING * 2}px)`

/** Natural width of the Facebook mobile-feed render; the preview is scaled, not reflowed. */
const PREVIEW_WIDTH = 360

/**
 * The ad's column is a fixed size rather than a percentage of the dialog. A phone
 * render has a natural width, and pinning it is what lets the history column open
 * without the ad resizing under a reviewer mid-decision. Wide enough to hold the
 * lead card's phone chrome too, which is a different frame from the ad's.
 */
const AD_COLUMN_WIDTH = 440

/** Below this the two columns can't both hold their minimums; the layout drops to one. */
const COPY_COLUMN_MIN = 380

/** Line length, not track width — a wide dialog leaves whitespace rather than 800px inputs. */
const COPY_CONTENT_MAX = 520

const COLUMN_GAP = 20

/** Dialog widths by the regions present, so a two-column client dialog isn't sized for three. */
const CLIENT_WIDTH = AD_COLUMN_WIDTH + COLUMN_GAP + COPY_CONTENT_MAX + SHELL_PADDING * 2
const EMPLOYEE_WIDTH = CLIENT_WIDTH + COLUMN_GAP + 360

/**
 * The narrow composition's floor: both columns at their minimums, plus the shell's
 * padding and the overlay's inset. Deliberately not `useScreenSize` — that only
 * knows about phone width, and everything between it and this would clip.
 */
const TWO_COLUMN_MIN = AD_COLUMN_WIDTH + COLUMN_GAP + COPY_COLUMN_MIN + SHELL_PADDING * 2 + 32

/**
 * The preview is allowed to grow past its natural size — the ad is the thing
 * being judged, and a 360px phone render in a full-height column otherwise
 * leaves most of that column empty. Capped because the ad's media is a bitmap
 * and goes soft well before the column runs out of room.
 */
const MAX_PREVIEW_SCALE = 1.45

const ctaLabel = (value?: string) =>
  value
    ? value
        .split('_')
        .map(word => word[0] + word.slice(1).toLowerCase())
        .join(' ')
    : undefined

/**
 * Shrink the ad preview to whatever height is left, instead of letting it decide
 * the dialog's height.
 *
 * It has to be measured rather than set: with a local fallback rendered the
 * preview's height is `auto` (the card's natural height at its fixed width), and
 * that varies by creative — a 4:5 image is far taller than a 1:1 — so no fixed
 * height fits every ad. Scaling also has to re-run after the fact, because the
 * journey step swaps in a differently-shaped frame.
 */
function useScaleToFit(deps: unknown[]) {
  const outerRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useLayoutEffect(() => {
    const outer = outerRef.current
    const inner = innerRef.current
    if (!outer || !inner) return undefined

    // `transform` doesn't affect layout size, so `scrollHeight` stays the natural
    // height and re-measuring can't feed back into itself.
    const measure = () => {
      const naturalHeight = inner.scrollHeight
      const naturalWidth = inner.scrollWidth
      if (!naturalHeight || !naturalWidth) return
      setScale(
        Math.min(
          outer.clientHeight / naturalHeight,
          outer.clientWidth / naturalWidth,
          MAX_PREVIEW_SCALE
        )
      )
    }

    measure()
    if (typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(measure)
    observer.observe(outer)
    observer.observe(inner)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return { outerRef, innerRef, scale }
}

/** One label/value row in the metadata panel; renders nothing when value is empty. */
const MetaRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div
      style={{
        fontFamily: typo.family.sans,
        fontSize: typo.size.eyebrow,
        fontWeight: typo.weight.semibold,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: dark.textFaint,
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: typo.family.sans,
        fontSize: typo.size.body,
        color: dark.text,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  </div>
)

const headerIconButtonStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  border: `1px solid ${dark.hairline}`,
  background: dark.surface,
  color: dark.textMuted,
  cursor: 'pointer',
}

/**
 * The client's full-screen-ish view of one ad: the ad at a real size beside the
 * copy Facebook will show, plus the same approve/reject actions as the card.
 * Rejecting reveals an inline reason field here instead of stacking a second
 * modal on top. A successful decision advances to the next ad (slideshow-style)
 * rather than closing — the parent decides what "next" means and closes instead
 * once there isn't one.
 *
 * One grid rather than nested flex rows, because five other issues each add a
 * region to this dialog and none of them can see the others. Wide and narrow
 * differ only in which areas the grid places, so the header and the verdict bar
 * are written once and never move between wrappers.
 *
 * The dialog is sized by the viewport and by which regions are present, not by
 * its contents. The verdict buttons used to sit at the end of the scrolling copy
 * column, which put the page's primary action below the fold on a laptop whenever
 * the copy ran long.
 */
export function ReviewCreativePreviewModal({
  creative,
  startInReject = false,
  position,
  themeLabel,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onSubmitVerdict,
  submitting,
  onRemoveApproval,
  onOpenSource,
  historySlot,
  onAdvance,
  onClose,
}: Props) {
  const [rejecting, setRejecting] = useState(startInReject)
  const [reason, setReason] = useState('')

  // Step two of the journey — reached deliberately, never automatically. Lead
  // cards repeat across a packet, so surfacing one on all fifty approvals is
  // noise rather than diligence.
  const [showJourney, setShowJourney] = useState(false)

  const isNarrow = !useMediaQuery(`(min-width:${TWO_COLUMN_MIN}px)`)
  const historyFits = useHistoryFits()
  const hasHistory = !!historySlot && historyFits && !isNarrow

  // A published ad is live on Facebook; Published already wins the computed status,
  // so the server refuses a verdict on one. Hide the actions rather than offer a
  // button that 400s.
  const canReview = creative.reviewStatus !== AssetApprovalStatus.Published
  const canRemoveApproval =
    !!onRemoveApproval && creative.reviewStatus === AssetApprovalStatus.Approved

  // Dynamic creatives have no single "the ad": Facebook assembles a combination
  // at delivery. Rather than list the parts, pin one combination at a time so the
  // reviewer judges assembled ads — which is what they are being asked to approve.
  const isDynamic = isDynamicCreative(creative)
  const pools = dynamicPools(creative)
  const poolList = copyPools(pools)
  const [requestedCombination, setCombination] = useState<DynamicCombination>(FIRST_COMBINATION)
  const combination = clampCombination(pools, requestedCombination)
  const shownCreative = isDynamic ? pinnedCombination(creative, pools, combination) : creative

  // Where the ad goes after the tap. The packet's own resolver rather than a
  // second implementation: it reads `leadCardUsed`, prefers `linkWebsiteURL` over
  // the Facebook permalink, and prefixes a scheme-less URL.
  const journey = getCreativeJourney(creative)
  const business = useGetBusinessIfExists()

  // Re-measure whenever the thing being measured can change shape: the journey
  // step swapping in a differently-sized frame, or the reason form opening under
  // it. The ad itself is fixed for the modal's lifetime — the parent remounts per
  // ad — so the creative is not a dependency.
  const { outerRef, innerRef, scale } = useScaleToFit([
    isDynamic,
    rejecting,
    showJourney,
    hasHistory,
  ])

  const trimmedReason = reason.trim()

  const handleApprove = async () => {
    await onSubmitVerdict({ approved: true })
    onAdvance()
  }

  const handleSubmitReject = async () => {
    if (!trimmedReason || submitting) return
    await onSubmitVerdict({ approved: false, notes: trimmedReason })
    onAdvance()
  }

  const gridStyle: React.CSSProperties = isNarrow
    ? {
        display: 'grid',
        height: BODY_HEIGHT,
        gap: COLUMN_GAP,
        gridTemplateColumns: 'minmax(0, 1fr)',
        // A dynamic ad's pools stack under it rather than being dropped: they are
        // the only place its other variants exist, and clients — the cohort most
        // likely to be on a phone — would otherwise approve copy they never saw.
        // The ad keeps the larger share; the pools scroll in what is left.
        gridTemplateRows: isDynamic
          ? 'auto minmax(0, 1.4fr) minmax(0, 1fr) auto'
          : 'auto minmax(0, 1fr) auto',
        gridTemplateAreas: isDynamic
          ? '"header" "preview" "copy" "actions"'
          : '"header" "preview" "actions"',
      }
    : {
        display: 'grid',
        height: BODY_HEIGHT,
        gap: COLUMN_GAP,
        gridTemplateColumns: hasHistory
          ? `${AD_COLUMN_WIDTH}px minmax(${COPY_COLUMN_MIN}px, 1fr) auto`
          : `${AD_COLUMN_WIDTH}px minmax(${COPY_COLUMN_MIN}px, 1fr)`,
        gridTemplateRows: 'auto minmax(0, 1fr) auto',
        gridTemplateAreas: hasHistory
          ? '"preview header history" "preview copy history" "preview actions history"'
          : '"preview header" "preview copy" "preview actions"',
      }

  return (
    <PacketModalShell
      palette={dark}
      hideHeader
      onClose={onClose}
      maxWidth={historySlot && historyFits ? EMPLOYEE_WIDTH : CLIENT_WIDTH}
      padding={SHELL_PADDING}
      closeOnBackdropClick
    >
      <div style={gridStyle}>
        {/* The ad owns its area outright, top to bottom. It is the thing being
          judged, so nothing shares its side and nothing above it steals height. */}
        <div
          style={{
            gridArea: 'preview',
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div
            ref={outerRef}
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: dark.bg,
              border: `1px solid ${dark.hairline}`,
              borderRadius: radius.md,
              padding: 12,
              overflow: 'hidden',
            }}
          >
            {showJourney && journey ? (
              <div ref={innerRef} style={{ transform: `scale(${scale})` }}>
                <CreativeJourneyPanel journey={journey} business={business} palette={dark} />
              </div>
            ) : (
              <div
                ref={innerRef}
                style={{
                  width: PREVIEW_WIDTH,
                  transform: `scale(${scale})`,
                  transformOrigin: 'center center',
                }}
              >
                {/* Our own rendering, never Facebook's. Facebook's preview of an
                  unlaunched ad is our stored spec handed back to us — drawn without
                  the enhancement opt-out the real ad ships with — so it is not the
                  authority it looks like, and waiting on it cost a round-trip per ad
                  across a fifty-ad packet the client walks alone. */}
                <LocalAdPreview
                  creative={shownCreative}
                  pageName={business?.businessName}
                  pageImageURL={business?.fbPageImgURL}
                />
              </div>
            )}
          </div>

          {/* The journey is a second step of the same phone, walked deliberately.
            The control lives beside the frame rather than on it: an overlay to
            catch a tap on the ad would swallow video playback and carousel
            swiping, and the ad is the one thing the reviewer is judging. */}
          {journey && (
            <button
              type="button"
              onClick={() => setShowJourney(open => !open)}
              style={{
                alignSelf: 'center',
                flexShrink: 0,
                padding: '6px 14px',
                borderRadius: radius.sm,
                border: `1px solid ${dark.hairline}`,
                background: dark.surface,
                fontFamily: typo.family.sans,
                fontSize: typo.size.body,
                color: dark.textMuted,
                cursor: 'pointer',
              }}
            >
              {showJourney
                ? '← Back to the ad'
                : journey.kind === 'lead-form'
                ? 'See the lead form →'
                : 'See where it goes →'}
            </button>
          )}
        </div>

        {/* Status and position sit over the copy rather than across the dialog,
          so the ad's column starts at the very top. */}
        <div
          style={{
            gridArea: 'header',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            minWidth: 0,
          }}
        >
          <StatusPill creative={creative} />
          <div
            style={{
              minWidth: 0,
              fontFamily: typo.family.sans,
              fontSize: typo.size.eyebrow,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: dark.textFaint,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Ad {position.current} of {position.total}
            {themeLabel ? ` · ${themeLabel}` : ''}
          </div>
          {/* Prev/next belong to the dialog, not the screen. Pinned to the viewport
            edges they float over the ad itself on anything short of a wide
            desktop, which is most of the laptops this is reviewed on. */}
          <button
            type="button"
            onClick={onPrev}
            disabled={!hasPrev}
            aria-label="Previous"
            style={{
              ...headerIconButtonStyle,
              marginLeft: 'auto',
              opacity: hasPrev ? 1 : 0.4,
              cursor: hasPrev ? 'pointer' : 'default',
            }}
          >
            <i className="fa fa-chevron-left" />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!hasNext}
            aria-label="Next"
            style={{
              ...headerIconButtonStyle,
              opacity: hasNext ? 1 : 0.4,
              cursor: hasNext ? 'pointer' : 'default',
            }}
          >
            <i className="fa fa-chevron-right" />
          </button>
          <button type="button" onClick={onClose} aria-label="Close" style={headerIconButtonStyle}>
            <i className="fa fa-times" />
          </button>
        </div>

        {/* A static ad's copy is already drawn on the ad, so this column is a
          reference and drops at narrow widths — employees review on laptops, so
          copy editing never has to work at phone width. A dynamic ad's pools are
          not a duplicate of anything: they hold the variants the preview can only
          show one of, so they survive the narrow layout. */}
        {(!isNarrow || isDynamic) && (
          <div
            style={{
              gridArea: 'copy',
              minWidth: 0,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              maxWidth: COPY_CONTENT_MAX,
            }}
          >
            {isDynamic ? (
              <DynamicPoolPicker
                assets={pools.assets}
                pools={poolList}
                at={combination}
                onPick={setCombination}
              />
            ) : (
              <>
                {creative.primaryText && (
                  <MetaRow label="Primary text">{creative.primaryText}</MetaRow>
                )}
                {creative.linkHeadline && (
                  <MetaRow label="Headline">{creative.linkHeadline}</MetaRow>
                )}
                {creative.linkDescription && (
                  <MetaRow label="Description">{creative.linkDescription}</MetaRow>
                )}
                {ctaLabel(creative.callToAction) && (
                  <MetaRow label="Call to action">{ctaLabel(creative.callToAction)}</MetaRow>
                )}
              </>
            )}

            {/* Copy editing lands here later, and media later still, so a rejection
              about the wrong image still needs somewhere to go in the meantime. */}
            {onOpenSource && (
              <button
                type="button"
                onClick={onOpenSource}
                style={{
                  alignSelf: 'flex-start',
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  fontFamily: typo.family.sans,
                  fontSize: typo.size.body,
                  color: dark.accent,
                  cursor: 'pointer',
                }}
              >
                Open in Creative Wizard →
              </button>
            )}
          </div>
        )}

        {hasHistory && <div style={{ gridArea: 'history', minHeight: 0 }}>{historySlot}</div>}

        {/* Pinned: a verdict is the point of the page, so it never scrolls away. */}
        {(canReview || rejecting) && (
          <div
            style={{
              gridArea: 'actions',
              borderTop: `1px solid ${dark.hairline}`,
              paddingTop: 14,
            }}
          >
            {rejecting ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label
                  style={{
                    fontFamily: typo.family.sans,
                    fontSize: typo.size.body,
                    fontWeight: typo.weight.semibold,
                    color: dark.text,
                  }}
                >
                  Reason *
                </label>
                <textarea
                  autoFocus
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={2}
                  placeholder="What should change?"
                  style={{ ...modalInputStyle(dark), resize: 'none' }}
                />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => setRejecting(false)}
                    disabled={submitting}
                    style={modalGhostButtonStyle(dark)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitReject}
                    disabled={!trimmedReason || submitting}
                    style={{
                      ...modalPrimaryButtonStyle(dark),
                      flex: '0 0 auto',
                      background: statusColors.danger,
                      borderColor: statusColors.danger,
                      opacity: !trimmedReason || submitting ? 0.6 : 1,
                      cursor: !trimmedReason || submitting ? 'default' : 'pointer',
                    }}
                  >
                    Submit rejection
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={submitting}
                  style={{
                    ...modalPrimaryButtonStyle(dark),
                    flex: '0 0 auto',
                    padding: '8px 28px',
                    background: statusColors.success,
                    borderColor: statusColors.success,
                    // Dark ink on the bright success fill: white over #22c55e is
                    // barely 2.3:1.
                    color: dark.accentInk,
                    opacity: submitting ? 0.6 : 1,
                  }}
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => setRejecting(true)}
                  disabled={submitting}
                  style={{
                    ...modalGhostButtonStyle(dark),
                    color: statusColors.danger,
                    borderColor: statusColors.danger,
                    opacity: submitting ? 0.6 : 1,
                  }}
                >
                  Reject
                </button>
                {/* Subordinate to the two verdicts: undoing a recorded decision is
                  rarer than making one. It had no home at all before the deck's
                  preview action bar retired. */}
                {canRemoveApproval && (
                  <button
                    type="button"
                    onClick={onRemoveApproval}
                    disabled={submitting}
                    style={{
                      marginLeft: 'auto',
                      padding: 0,
                      border: 'none',
                      background: 'transparent',
                      fontFamily: typo.family.sans,
                      fontSize: typo.size.body,
                      color: dark.textMuted,
                      cursor: submitting ? 'default' : 'pointer',
                      opacity: submitting ? 0.6 : 1,
                    }}
                  >
                    Remove approval
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </PacketModalShell>
  )
}
